import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CreditCard,
  Calendar,
  AlertTriangle,
  FileText,
  Receipt,
  CheckCircle
} from 'lucide-react';
import { SortableHeader } from '@/components/ui/sortable-header';
import { Pagination } from '@/components/ui/pagination';
import { useTableData } from '@/hooks/useTableData';
import { accountsPayableService } from '@/services/accountsPayableService';
import type { AccountPayable } from '@/types/accounts-payable.types';

interface AccountsPayableTableProps {
  accounts: AccountPayable[];
  loading: boolean;
  onPaymentClick: (account: AccountPayable) => void;
  onViewClick: (account: AccountPayable) => void;
  onInvoiceEntryClick: (account: AccountPayable) => void;
}

type AccountPayableSortField = 'accountNumber' | 'supplierName' | 'receivedAmount' | 'pendingAmount' | 'dueDate' | 'createdAt';

export const AccountsPayableTable: React.FC<AccountsPayableTableProps> = ({
  accounts,
  loading,
  onPaymentClick,
  onViewClick,
  onInvoiceEntryClick
}) => {
  // Configuración de etiquetas para los campos de ordenamiento
  const fieldLabels: Record<AccountPayableSortField, string> = {
    accountNumber: 'Número de Cuenta',
    supplierName: 'Proveedor',
    receivedAmount: 'Monto Recibido',
    pendingAmount: 'Saldo Pendiente',
    dueDate: 'Fecha Vencimiento',
    createdAt: 'Creado'
  };

  // Extractor de valores para ordenamiento
  const sortValueExtractor = (account: AccountPayable, field: AccountPayableSortField): any => {
    switch (field) {
      case 'accountNumber':
        return account.accountNumber.toLowerCase();
      case 'supplierName':
        return account.supplierName.toLowerCase();
      case 'receivedAmount':
        return account.receivedAmount;
      case 'pendingAmount':
        return account.pendingAmount;
      case 'dueDate':
        return account.dueDate ? new Date(account.dueDate) : new Date(0);
      case 'createdAt':
        return new Date(account.createdAt);
      default:
        return account.accountNumber;
    }
  };

  // Usar el hook estandarizado de tabla
  const tableData = useTableData<AccountPayable, AccountPayableSortField>(
    {
      data: accounts,
      defaultSortField: 'createdAt',
      defaultSortDirection: 'desc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-macartur-primary mx-auto mb-4"></div>
        <p className="text-gray-600">Cargando cuentas por pagar...</p>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="text-center py-12">
        <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay cuentas por pagar</h3>
        <p className="text-gray-500">No se encontraron cuentas por pagar con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="accountNumber"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Número de Cuenta
              </SortableHeader>
              <SortableHeader
                field="supplierName"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Proveedor
              </SortableHeader>
              <SortableHeader
                field="receivedAmount"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-right"
              >
                Monto Recibido
              </SortableHeader>
              <SortableHeader
                field="pendingAmount"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-right"
              >
                Saldo Pendiente
              </SortableHeader>
              <th className="px-4 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <SortableHeader
                field="dueDate"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Fecha Vencimiento
              </SortableHeader>
              <SortableHeader
                field="createdAt"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Creado
              </SortableHeader>
              <th className="px-4 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((account) => {
                const isOverdue = accountsPayableService.isOverdue(account);
                const canPayment = account.pendingAmount > 0 && account.hasInvoice;
                const needsInvoice = !account.hasInvoice;

                return (
                  <tr
                    key={account.id}
                    className={`hover:bg-gray-50 ${isOverdue ? 'bg-red-50' : ''}`}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {account.accountNumber}
                        </span>
                        {isOverdue && (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        OC: {account.purchaseOrderNumber}
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <span className="font-medium text-gray-900">
                        {account.supplierName}
                      </span>
                    </td>

                    <td className="px-4 py-4 text-right">
                      <div className={`font-medium ${
                        account.receivedAmount !== account.originalAmount 
                          ? 'text-amber-600' 
                          : 'text-gray-900'
                      }`}>
                        {accountsPayableService.formatCurrency(account.receivedAmount)}
                      </div>
                      {account.receivedAmount !== account.originalAmount && (
                        <div className="text-xs text-amber-600">
                          Diferencia: {accountsPayableService.formatCurrency(account.receivedAmount - account.originalAmount)}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      <div className={`font-medium ${
                        account.pendingAmount === 0 
                          ? 'text-green-600' 
                          : isOverdue 
                            ? 'text-red-600' 
                            : 'text-yellow-600'
                      }`}>
                        {accountsPayableService.formatCurrency(account.pendingAmount)}
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-1">
                        <Badge 
                          className={accountsPayableService.getStatusColor(account.status)}
                          variant="secondary"
                        >
                          {accountsPayableService.getStatusText(account.status)}
                        </Badge>
                        {isOverdue && (
                          <Badge variant="destructive" className="text-xs">
                            Vencida
                          </Badge>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {account.dueDate 
                            ? accountsPayableService.formatDate(account.dueDate)
                            : 'No definida'
                          }
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-600">
                        {accountsPayableService.formatDate(account.createdAt)}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {needsInvoice && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => onInvoiceEntryClick(account)}
                            className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700"
                          >
                            <Receipt className="h-3 w-3" />
                            Ingresar Factura
                          </Button>
                        )}
                        
                        {account.hasInvoice && (
                          <div className="flex items-center gap-1 text-green-600 text-xs">
                            <CheckCircle className="h-3 w-3" />
                            <span>Factura Ingresada</span>
                          </div>
                        )}
                        
                        {canPayment && (
                          <Button
                            size="sm"
                            variant={isOverdue ? "destructive" : "default"}
                            onClick={() => onPaymentClick(account)}
                            className="flex items-center gap-1"
                          >
                            <CreditCard className="h-3 w-3" />
                            Pagar
                          </Button>
                        )}
                        
                        {account.pendingAmount > 0 && !account.hasInvoice && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled
                            className="flex items-center gap-1 opacity-50"
                            title="Debe ingresar la factura antes de poder pagar"
                          >
                            <CreditCard className="h-3 w-3" />
                            Pagar
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onViewClick(account)}
                          className="flex items-center gap-1"
                        >
                          <FileText className="h-3 w-3" />
                          Ver
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};